import { AffineContext } from '@affine/core/components/context';
import { AppContainer } from '@affine/core/desktop/components/app-container';
import { router } from '@affine/core/desktop/router';
import { configureCommonModules } from '@affine/core/modules';
import { I18nProvider } from '@affine/core/modules/i18n';
import { LifecycleService } from '@affine/core/modules/lifecycle';
import {
  setupPerformanceMonitoring,
  setupWorkerMonitoring,
} from '@affine/core/modules/performance';
import {
  configureLocalStorageStateStorageImpls,
  NbstoreProvider,
} from '@affine/core/modules/storage';
import { PopupWindowProvider } from '@affine/core/modules/url';
import { configureBrowserWorkbenchModule } from '@affine/core/modules/workbench';
import { configureBrowserWorkspaceFlavours } from '@affine/core/modules/workspace-engine';
import createEmotionCache from '@affine/core/utils/create-emotion-cache';
import { getWorkerUrl } from '@affine/env/worker';
import { StoreManagerClient } from '@affine/nbstore/worker/client';
import { CacheProvider } from '@emotion/react';
import { Framework, FrameworkRoot, getCurrentStore } from '@toeverything/infra';
import { OpClient } from '@toeverything/infra/op';
import { Suspense, useEffect } from 'react';
import { RouterProvider } from 'react-router-dom';

const cache = createEmotionCache();

let storeManagerClient: StoreManagerClient;

const workerUrl = getWorkerUrl('nbstore');

// 创建 framework（需要在 worker 监控之前创建）
const framework = new Framework();
configureCommonModules(framework);
configureBrowserWorkbenchModule(framework);
configureLocalStorageStateStorageImpls(framework);
configureBrowserWorkspaceFlavours(framework);

// 初始化性能监控（非阻塞）
setupPerformanceMonitoring(framework);

// 创建并监控 worker
if (
  window.SharedWorker &&
  localStorage.getItem('disableSharedWorker') !== 'true'
) {
  const worker = new SharedWorker(workerUrl, {
    name: 'affine-shared-worker',
  });
  const monitoredWorker = setupWorkerMonitoring(worker, framework);
  storeManagerClient = new StoreManagerClient(
    new OpClient(monitoredWorker.port)
  );
} else {
  const worker = new Worker(workerUrl);
  const monitoredWorker = setupWorkerMonitoring(worker, framework);
  storeManagerClient = new StoreManagerClient(new OpClient(monitoredWorker));
}
window.addEventListener('beforeunload', () => {
  storeManagerClient.dispose();
});
window.addEventListener('focus', () => {
  storeManagerClient.disableBatterySaveMode();
});
window.addEventListener('click', () => {
  storeManagerClient.disableBatterySaveMode();
});
window.addEventListener('blur', () => {
  storeManagerClient.enableBatterySaveMode();
});

const future = {
  v7_startTransition: true,
} as const;

// framework 已在上面创建和配置
framework.impl(NbstoreProvider, {
  openStore(key, options) {
    return storeManagerClient.open(key, options);
  },
});
framework.impl(PopupWindowProvider, {
  open: (target: string) => {
    const targetUrl = new URL(target);

    let url: string;
    // safe to open directly if in the same origin
    if (targetUrl.origin === location.origin) {
      url = target;
    } else {
      const redirectProxy = location.origin + '/redirect-proxy';
      const search = new URLSearchParams({
        redirect_uri: target,
      });

      url = `${redirectProxy}?${search.toString()}`;
    }
    window.open(url, '_blank', 'popup noreferrer noopener');
  },
});
const frameworkProvider = framework.provider();

// setup application lifecycle events, and emit application start event
window.addEventListener('focus', () => {
  frameworkProvider.get(LifecycleService).applicationFocus();
});
frameworkProvider.get(LifecycleService).applicationStart();

export function App() {
  // 新增 IconPark 脚本加载逻辑
  useEffect(() => {
    if (!document.querySelector('#iconpark-script')) {
      const script = document.createElement('script');
      script.id = 'iconpark-script';
      script.src =
        'https://lf1-cdn-tos.bytegoofy.com/obj/iconpark/svg_37669_249.c7fa05bf9c985d1b806b1945e892f62e.js';
      script.defer = true;
      document.head.append(script);
    }
  }, []);
  return (
    <Suspense>
      <FrameworkRoot framework={frameworkProvider}>
        <CacheProvider value={cache}>
          <I18nProvider>
            <AffineContext store={getCurrentStore()}>
              <RouterProvider
                fallbackElement={<AppContainer fallback />}
                router={router}
                future={future}
              />
            </AffineContext>
          </I18nProvider>
        </CacheProvider>
      </FrameworkRoot>
    </Suspense>
  );
}
